import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LANGUAGES } from "@/lib/i18n";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader } from "@/components/PageParts";
import { Button, Card, Input, Badge } from "@/components/ui";

function MessagesInbox({ isAr }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.myMessages().then((r) => { if (alive) setItems(r.items || []); }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  const markRead = async (m) => {
    try { await api.markMessageRead(m.id); setItems((xs) => xs.map((x) => (x.id === m.id ? { ...x, read: true } : x))); } catch { /* ignore */ }
  };
  if (loading || !items.length) return null;
  return (
    <Card>
      <p className="mb-3 text-sm font-black">{isAr ? "رسائلي من الإدارة" : "Messages from admin"}</p>
      <div className="space-y-2">
        {items.map((m) => (
          <div key={m.id} className={`rounded-xl border p-3 ${m.read ? "border-border bg-card/30" : "border-primary/40 bg-primary/5"}`} data-testid="user-message">
            <div className="flex items-center justify-between gap-2">
              <p className="font-bold">{m.title}</p>
              {!m.read && <button onClick={() => markRead(m)} className="text-xs font-bold text-primary" data-testid="mark-read">{isAr ? "تعليم كمقروء" : "Mark read"}</button>}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{m.body}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Account() {
  const { user, lang, setLang, signOut, refreshUser } = useAuth();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [delPass, setDelPass] = useState("");
  const [busy, setBusy] = useState(null);
  const fp0 = user?.firm_profile || {};
  const [firm, setFirm] = useState({
    firm_name: fp0.firm_name || "",
    engineer_name: fp0.engineer_name || "",
    license_no: fp0.license_no || "",
    phone: fp0.phone || "",
    email: fp0.email || "",
    city: fp0.city || "",
    logo_base64: fp0.logo_base64 || "",
  });

  function onLogoPick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error(isAr ? "الرجاء اختيار صورة" : "Please pick an image"); return; }
    if (file.size > 800 * 1024) { toast.error(isAr ? "حجم الشعار كبير (الحد 800KB)" : "Logo too large (max 800KB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setFirm((f) => ({ ...f, logo_base64: String(reader.result) }));
    reader.readAsDataURL(file);
  }
  async function saveFirm() {
    setBusy("firm");
    try { await api.updateSettings({ firm_profile: firm }); await refreshUser(); toast.success(isAr ? "تم حفظ بيانات المكتب" : "Firm details saved"); }
    catch (e) { toast.error(String(e.message || e)); } finally { setBusy(null); }
  }

  const { data: sub } = useDeliverable(() => api.getMySubscription(), []);
  const { data: devices, reload: reloadDevices } = useDeliverable(() => api.myDevices(), []);

  async function saveName() {
    setBusy("name");
    try { await api.updateSettings({ full_name: fullName }); await refreshUser(); toast.success(isAr ? "تم الحفظ" : "Saved"); }
    catch (e) { toast.error(String(e.message || e)); } finally { setBusy(null); }
  }
  async function changePw() {
    if (!cur || !next) return;
    setBusy("pw");
    try { await api.changePassword(cur, next); setCur(""); setNext(""); toast.success(isAr ? "تم تغيير كلمة المرور" : "Password changed"); }
    catch (e) { toast.error(String(e.message || e)); } finally { setBusy(null); }
  }
  async function delAcc() {
    if (!window.confirm(isAr ? "حذف الحساب نهائياً؟" : "Permanently delete account?")) return;
    setBusy("del");
    try { await api.deleteAccount(delPass); signOut(); navigate("/landing"); }
    catch (e) { toast.error(String(e.message || e)); } finally { setBusy(null); }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <PageHeader eyebrow={isAr ? "الحساب" : "Account"} title={isAr ? "حسابي" : "My account"} right={sub && <Badge tone={sub.is_pro ? "success" : "muted"}>{sub.tier || (sub.is_pro ? "PRO" : "FREE")}</Badge>} />
      <div className="space-y-4">
        <MessagesInbox isAr={isAr} />
        <Card>
          <p className="mb-3 text-sm font-black">{isAr ? "الملف الشخصي" : "Profile"}</p>
          <div className="space-y-3">
            <Input label={isAr ? "البريد الإلكتروني" : "Email"} value={user?.email || ""} disabled />
            <Input label={isAr ? "الاسم الكامل" : "Full name"} value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="account-name" />
            <Button size="sm" loading={busy === "name"} onClick={saveName} data-testid="save-name-btn">{isAr ? "حفظ" : "Save"}</Button>
          </div>
        </Card>

        <Card data-testid="firm-branding-card">
          <p className="mb-1 text-sm font-black">{isAr ? "هوية المكتب (اختياري)" : "Office branding (optional)"}</p>
          <p className="mb-3 text-xs text-muted-foreground">{isAr ? "يظهر الشعار والبيانات في بلوك العنوان وملفات PDF. يمكنك تغييره في أي وقت." : "Logo & details appear in the title block and PDF outputs. Change any time."}</p>
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-border bg-white">
              {firm.logo_base64
                ? <img src={firm.logo_base64.startsWith("data:") ? firm.logo_base64 : `data:image/png;base64,${firm.logo_base64}`} alt="logo" className="h-full w-full object-contain p-1" data-testid="firm-logo-preview" />
                : <span className="text-[10px] text-muted-foreground">{isAr ? "لا شعار" : "No logo"}</span>}
            </div>
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer rounded-full border border-primary bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary" data-testid="firm-logo-upload">
                {isAr ? "رفع شعار" : "Upload logo"}
                <input type="file" accept="image/*" className="hidden" onChange={onLogoPick} />
              </label>
              {firm.logo_base64 && (
                <button onClick={() => setFirm((f) => ({ ...f, logo_base64: "" }))} className="text-xs font-bold text-red-400" data-testid="firm-logo-remove">{isAr ? "إزالة الشعار" : "Remove logo"}</button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input label={isAr ? "اسم المكتب" : "Firm name"} value={firm.firm_name} onChange={(e) => setFirm((f) => ({ ...f, firm_name: e.target.value }))} data-testid="firm-name" />
            <Input label={isAr ? "اسم المهندس" : "Engineer name"} value={firm.engineer_name} onChange={(e) => setFirm((f) => ({ ...f, engineer_name: e.target.value }))} data-testid="firm-engineer" />
            <Input label={isAr ? "رقم الترخيص" : "License no."} value={firm.license_no} onChange={(e) => setFirm((f) => ({ ...f, license_no: e.target.value }))} data-testid="firm-license" />
            <Input label={isAr ? "المدينة" : "City"} value={firm.city} onChange={(e) => setFirm((f) => ({ ...f, city: e.target.value }))} data-testid="firm-city" />
            <Input label={isAr ? "الهاتف" : "Phone"} value={firm.phone} onChange={(e) => setFirm((f) => ({ ...f, phone: e.target.value }))} data-testid="firm-phone" />
            <Input label={isAr ? "البريد" : "Email"} value={firm.email} onChange={(e) => setFirm((f) => ({ ...f, email: e.target.value }))} data-testid="firm-email" />
          </div>
          <Button size="sm" className="mt-3" loading={busy === "firm"} onClick={saveFirm} data-testid="save-firm-btn">{isAr ? "حفظ بيانات المكتب" : "Save firm details"}</Button>
        </Card>

        <Card>
          <p className="mb-3 text-sm font-black">{isAr ? "اللغة" : "Language"}</p>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)} className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${l.code === lang ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>{l.flag} {l.nativeName}</button>
            ))}
          </div>
        </Card>

        {user?.role !== "guest" && (
          <Card>
            <p className="mb-3 text-sm font-black">{isAr ? "تغيير كلمة المرور" : "Change password"}</p>
            <div className="space-y-3">
              <Input type="password" label={isAr ? "الحالية" : "Current"} value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-pw" />
              <Input type="password" label={isAr ? "الجديدة" : "New"} value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-pw" />
              <Button size="sm" loading={busy === "pw"} onClick={changePw} data-testid="change-pw-btn">{isAr ? "تحديث" : "Update"}</Button>
            </div>
          </Card>
        )}

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-black">{isAr ? "الأجهزة المسجّلة" : "Registered devices"}</p>
            <Button size="sm" variant="ghost" onClick={reloadDevices}>{isAr ? "تحديث" : "Refresh"}</Button>
          </div>
          <div className="space-y-2">
            {(devices?.items || []).map((d) => (
              <div key={d.device_id || d.id} className="flex items-center justify-between rounded-lg bg-black/20 px-3 py-2 text-sm">
                <span className="truncate">{d.os || d.platform || d.device_id}</span>
                <button onClick={async () => { try { await api.revokeMyDevice(d.device_id || d.id); reloadDevices(); } catch (e) { toast.error(String(e.message || e)); } }} className="text-xs font-bold text-red-400">{isAr ? "إلغاء" : "Revoke"}</button>
              </div>
            ))}
            {(devices?.items || []).length === 0 && <p className="text-xs text-muted-foreground">—</p>}
          </div>
        </Card>

        <Card className="border-destructive/40">
          <p className="mb-3 text-sm font-black text-red-400">{isAr ? "حذف الحساب" : "Delete account"}</p>
          <div className="space-y-3">
            <Input type="password" label={isAr ? "أكد كلمة المرور" : "Confirm password"} value={delPass} onChange={(e) => setDelPass(e.target.value)} data-testid="del-pw" />
            <Button size="sm" variant="danger" loading={busy === "del"} onClick={delAcc} data-testid="delete-account-btn">{isAr ? "حذف نهائي" : "Delete permanently"}</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
